import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
// Don't import Stripe at the top level
// import { loadStripe } from '@stripe/stripe-js';

// Check if user is on iOS device - we'll avoid showing subscription UI on iOS
export const isIosDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
};

// Initialize Stripe only when needed and if not on iOS
const getStripe = async () => {
  // Check if on iOS device - return null if true
  if (isIosDevice()) {
    console.log('iOS device detected, skipping Stripe initialization');
    return null;
  }

  try {
    // Dynamically import Stripe only when needed
    const { loadStripe } = await import('@stripe/stripe-js');
    return await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  } catch (error) {
    console.error('Error loading Stripe:', error);
    return null;
  }
};

// Instead of initializing at the top level
// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Get current user's subscription status
export const getSubscriptionStatus = async () => {
  try {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const db = getFirestore();
    const subscriptionRef = doc(db, 'subscriptions', userId);
    const subscriptionDoc = await getDoc(subscriptionRef);
    
    if (!subscriptionDoc.exists()) {
      // User has no subscription record - create a default document
      // We no longer automatically create a trial - trials now start only when a TV is linked
      const defaultSubscription = {
        userId: userId,
        status: 'none', // Using 'none' to indicate no subscription or trial
        createdAt: Timestamp.now(),
        hasHadFreeTrial: false // Track if user has ever had a free trial
      };
      
      await setDoc(subscriptionRef, defaultSubscription);
      
      return {
        ...defaultSubscription,
        isActive: false,
        isTrial: false
      };
    }
    
    // User has a subscription record
    const subscriptionData = subscriptionDoc.data();
    const now = new Date();
    
    // Calculate trial days remaining if in trial
    let daysRemaining = 0;
    if (subscriptionData.trialEnd) {
      const trialEndDate = subscriptionData.trialEnd.toDate();
      daysRemaining = Math.max(0, Math.ceil((trialEndDate - now) / (1000 * 60 * 60 * 24)));
    }
    
    // Determine if subscription is active
    const isActive = subscriptionData.status === 'active' || 
                    (subscriptionData.status === 'trialing' && daysRemaining > 0);
    
    // Get expiration date for active subscriptions
    let expiresAt = null;
    if (subscriptionData.currentPeriodEnd) {
      expiresAt = subscriptionData.currentPeriodEnd.toDate();
    }
    
    return {
      ...subscriptionData,
      isActive,
      isTrial: subscriptionData.status === 'trialing',
      isCustomTrial: subscriptionData.isCustomTrial || false,
      hasHadFreeTrial: subscriptionData.hasHadFreeTrial || false,
      daysRemaining,
      expiresAt
    };
  } catch (error) {
    console.error('Error getting subscription status:', error);
    // Return default values in case of error
    return {
      status: 'error',
      isActive: false,
      isTrial: false,
      hasHadFreeTrial: false,
      error: error.message
    };
  }
};

// Create a Stripe Checkout Session for a new subscription
export const createCheckoutSession = async () => {
  // Prevent iOS users from creating checkout sessions
  if (isIosDevice()) {
    return {
      success: false,
      error: 'Subscription management is not available on iOS devices'
    };
  }

  try {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const functions = getFunctions();
    const createStripeCheckout = httpsCallable(functions, 'createStripeCheckout');
    
    // Call Firebase function to create a checkout session
    const { data } = await createStripeCheckout({
      price: process.env.REACT_APP_STRIPE_PRICE_ID, // Monthly subscription price ID
      successUrl: `${window.location.origin}/settings/subscription/success`,
      cancelUrl: `${window.location.origin}/settings/subscription/cancel`
    });
    
    // Get Stripe instance dynamically
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Unable to initialize Stripe');
    }
    
    const result = await stripe.redirectToCheckout({
      sessionId: data.sessionId
    });
    
    if (result.error) {
      throw result.error;
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error creating checkout session:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Handle successful subscription
export const handleSubscriptionSuccess = async () => {
  try {
    // Refresh the subscription data from server
    const subscriptionStatus = await getSubscriptionStatus();
    return { success: true, subscription: subscriptionStatus };
  } catch (error) {
    console.error('Error handling subscription success:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Cancel subscription
export const cancelSubscription = async () => {
  // Prevent iOS users from cancelling subscriptions directly
  if (isIosDevice()) {
    return {
      success: false,
      error: 'Subscription management is not available on iOS devices'
    };
  }

  try {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const functions = getFunctions();
    const cancelStripeSubscription = httpsCallable(functions, 'cancelStripeSubscription');
    
    const { data } = await cancelStripeSubscription();
    
    return { 
      success: true, 
      message: 'Subscription cancelled successfully',
      cancelAtPeriodEnd: data.cancelAtPeriodEnd,
      currentPeriodEnd: data.currentPeriodEnd
    };
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Reactivate cancelled subscription before period ends
export const reactivateSubscription = async () => {
  // Prevent iOS users from reactivating subscriptions directly
  if (isIosDevice()) {
    return {
      success: false,
      error: 'Subscription management is not available on iOS devices'
    };
  }

  try {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const functions = getFunctions();
    const reactivateStripeSubscription = httpsCallable(functions, 'reactivateStripeSubscription');
    
    const { data } = await reactivateStripeSubscription();
    
    return { 
      success: true, 
      message: 'Subscription reactivated successfully' 
    };
  } catch (error) {
    console.error('Error reactivating subscription:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Update customer payment method
export const updatePaymentMethod = async () => {
  // Prevent iOS users from updating payment methods
  if (isIosDevice()) {
    return {
      success: false,
      error: 'Subscription management is not available on iOS devices'
    };
  }

  try {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const functions = getFunctions();
    const createStripePortalSession = httpsCallable(functions, 'createStripePortalSession');
    
    const { data } = await createStripePortalSession({
      returnUrl: `${window.location.origin}/settings/subscription`
    });
    
    // Redirect to Stripe Customer Portal
    window.location.href = data.url;
    
    return { success: true };
  } catch (error) {
    console.error('Error updating payment method:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Start a free trial manually (without requiring payment)
export const startFreeTrial = async () => {
  try {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const functions = getFunctions();
    const startFreeTrialFn = httpsCallable(functions, 'startFreeTrial');
    
    const { data } = await startFreeTrialFn();
    
    return { 
      success: true, 
      message: 'Free trial started successfully',
      trialData: data
    };
  } catch (error) {
    console.error('Error starting free trial:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

// Convert from trial to paid subscription
export const convertTrialToSubscription = async () => {
  // Prevent iOS users from upgrading trials directly
  if (isIosDevice()) {
    return {
      success: false,
      error: 'Subscription management is not available on iOS devices'
    };
  }

  try {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    
    if (!userId) {
      throw new Error('User not authenticated');
    }
    
    const functions = getFunctions();
    const convertTrialFn = httpsCallable(functions, 'convertTrialToSubscription');
    
    const { data } = await convertTrialFn();
    
    // If we got a sessionId, redirect to Stripe checkout
    if (data.sessionId) {
      // Get Stripe instance dynamically
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Unable to initialize Stripe');
      }
      
      await stripe.redirectToCheckout({
        sessionId: data.sessionId
      });
    }
    
    return { 
      success: true, 
      message: 'Redirecting to complete subscription',
      data
    };
  } catch (error) {
    console.error('Error converting trial to subscription:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
}; 